import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { useSetClientActive } from "@/hooks/useClients";
import { buildCtaExampleSnippet, buildWidgetSnippet } from "@/lib/widget-snippet";
import { WizardFooter } from "./WizardFooter";
import type { SuperAdminClient } from "@/lib/api";

type Props = {
  client: SuperAdminClient;
  onBack: (() => void) | null;
  onDone: () => void;
};

/**
 * Step 7 — activate the client and hand over the embed snippet.
 *
 * Activation is what puts the agent in front of real prospects: until now
 * `isActive = false` has kept the client's origins out of the CORS allow-list and its
 * users out of login, which is what made the draft state safe.
 *
 * The widget's initial-option chips are NOT here yet: `suggestInitialOptions` resolves
 * a deployment, which step 5 creates and step 5 does not exist. They are configured
 * from Agente → Ajustes meanwhile.
 */
function CopyButton({
  onClick,
  copied,
}: {
  onClick: () => void;
  copied: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-2 text-xs font-medium text-ink-700 hover:bg-ink-050"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? "Copiado" : "Copiar"}
    </button>
  );
}

export function StepActivate({ client, onBack, onDone }: Props): React.JSX.Element {
  const setActive = useSetClientActive();
  const [copied, setCopied] = useState<"embed" | "cta" | null>(null);

  const snippet = buildWidgetSnippet(client.id);
  const ctaSnippet = buildCtaExampleSnippet();
  const hasOrigins = client.widgetOrigins.length > 0;

  function handleCopy(text: string, which: "embed" | "cta"): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(which);
        setTimeout(() => setCopied(null), 2000);
      })
      .catch(() => toast.error("No se pudo copiar. Selecciona el texto manualmente."));
  }

  function handleActivate(): void {
    if (client.isActive) {
      onDone();
      return;
    }
    setActive.mutate(
      { id: client.id, isActive: true },
      {
        onSuccess: () => {
          toast.success("Cliente activado");
          onDone();
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "No se pudo activar el cliente"),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-ink-500">
        Al activar, el widget empieza a cargar en los dominios configurados y los
        usuarios del cliente pueden iniciar sesión.
      </p>

      <div className="rounded-lg border border-ink-100 bg-white p-4">
        <p className="text-[13px] font-medium text-ink-900">Código para el sitio</p>
        <p className="mt-0.5 text-[12px] text-ink-500">
          Pégalo antes de <code className="font-mono">&lt;/body&gt;</code>. Es el mismo
          para todas las páginas del sitio.
        </p>

        <div className="mt-3 flex items-start gap-2">
          <pre className="min-w-0 flex-1 overflow-x-auto rounded-md bg-ink-050 px-3 py-2 text-[12px] text-ink-900">
            <code>{snippet}</code>
          </pre>
          <CopyButton
            onClick={() => handleCopy(snippet, "embed")}
            copied={copied === "embed"}
          />
        </div>

        {/* Not framed as an error: a client that sells entirely over WhatsApp has no
            site to embed this in, and that is a valid configuration. */}
        {!hasOrigins && (
          <p className="mt-3 rounded-md bg-ink-050 px-3 py-2 text-[12px] text-ink-600">
            Este cliente no tiene orígenes configurados, así que el widget no cargará en
            ningún sitio (el navegador lo bloquea por CORS). Es lo esperado si solo usará
            WhatsApp; si no, agrega su dominio en el paso 1.
          </p>
        )}
      </div>

      <div className="rounded-lg border border-ink-100 bg-white p-4">
        <p className="text-[13px] font-medium text-ink-900">
          Opcional: un botón del sitio que abre el chat
        </p>
        <p className="mt-0.5 text-[12px] text-ink-500">
          Cualquier elemento con <code className="font-mono">data-scholaops-prompt</code>{" "}
          abre el widget y le hace esa pregunta al agente, como si el visitante la
          hubiera escrito. Sirve para los CTA que ya existen en el sitio.
        </p>

        <div className="mt-3 flex items-start gap-2">
          <pre className="min-w-0 flex-1 overflow-x-auto rounded-md bg-ink-050 px-3 py-2 text-[12px] text-ink-900">
            <code>{ctaSnippet}</code>
          </pre>
          <CopyButton onClick={() => handleCopy(ctaSnippet, "cta")} copied={copied === "cta"} />
        </div>

        <p className="mt-3 text-[12px] text-ink-500">
          El CRM guarda de qué botón llegó cada prospecto, así que conviene que cada CTA
          tenga su propia etiqueta en <code className="font-mono">data-scholaops-entry</code>.
        </p>
      </div>

      <div className="rounded-lg border border-ink-100 bg-white p-4">
        <p className="text-[13px] font-medium text-ink-900">Después de publicar</p>
        <ul className="mt-1.5 list-disc pl-5 text-[12px] text-ink-500">
          <li>Conectar WhatsApp (puede tardar días si es la API oficial).</li>
          <li>Invitar a los usuarios del cliente al CRM.</li>
          <li>Configurar las opciones iniciales del widget en Agente → Ajustes.</li>
        </ul>
        <p className="mt-1.5 text-[12px] text-ink-400">
          Nada de esto bloquea al agente en el sitio.
        </p>
      </div>

      <WizardFooter
        onBack={onBack}
        onContinue={handleActivate}
        canContinue
        isBusy={setActive.isPending}
        continueLabel={client.isActive ? "Finalizar" : "Activar y finalizar"}
        hint={client.isActive ? "Este cliente ya está activo." : undefined}
      />
    </div>
  );
}
